import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Icons from '../components/Icons';
import toast from 'react-hot-toast';
import * as api from '../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────
const TYPE_FILTERS = ['All', 'Image', 'Video'];

function isVideoTemplate(tmpl) {
  return tmpl?.type === 'video' || tmpl?.editType === 'text_to_video' || tmpl?.editType === 'frame-to-video';
}

function firstMedia(value) {
  return Array.isArray(value) ? value.find(Boolean) : value;
}

function templateMedia(tmpl) {
  return tmpl?.mediaUrl || firstMedia(tmpl?.refImageUrl) || tmpl?.startImageUrl || tmpl?.endImageUrl || null;
}

function TypeBadge({ isVideo }) {
  return (
    <div style={{
      position: 'absolute', top: 8, right: 8,
      fontSize: 9, fontWeight: 800,
      padding: '3px 9px', borderRadius: 99,
      backdropFilter: 'blur(8px)',
      background: isVideo ? 'rgba(34,211,238,.14)' : 'rgba(139,92,246,.14)',
      color: isVideo ? '#22d3ee' : '#a78bfa',
      border: `1px solid ${isVideo ? 'rgba(34,211,238,.3)' : 'rgba(139,92,246,.3)'}`,
      zIndex: 5,
    }}>
      {isVideo ? '▶ Video' : '🖼 Image'}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={{
      aspectRatio: '1',
      borderRadius: 14,
      background: 'var(--s1)',
      border: '1px solid var(--b1)',
      overflow: 'hidden',
      position: 'relative',
    }}>
      <div className="explore-skeleton-shimmer" style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.04) 50%, transparent 100%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.6s infinite',
      }} />
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function ExplorePage({ onShowAuth }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('All');
  const [search, setSearch] = useState('');
  const [usingId, setUsingId] = useState(null);
  const [lightboxItem, setLightboxItem] = useState(null);
  const [copied, setCopied] = useState(false);

  // ── Fetch templates ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.fetchTemplates()
      .then(data => { if (!cancelled) setTemplates(data); })
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Filter ───────────────────────────────────────────────────────────────
  const filtered = templates.filter(t => {
    const isVideo = isVideoTemplate(t);
    const matchTab =
      tab === 'All' ||
      (tab === 'Image' && !isVideo) ||
      (tab === 'Video' && isVideo);
    const matchSearch = !search.trim() ||
      (t.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.prompt || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.model || '').toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  // ── "Use" button handler ─────────────────────────────────────────────────
  const handleUse = useCallback(async (tmpl) => {
    if (!user) { if (onShowAuth) onShowAuth(); return; }
    setUsingId(tmpl.id || tmpl.$id);

    try {
      const isVideo = isVideoTemplate(tmpl);

      // refImageUrl can be stored as an array or a single string
      const refImageUrls = Array.isArray(tmpl.refImageUrl)
        ? tmpl.refImageUrl.filter(Boolean)
        : tmpl.refImageUrl ? [tmpl.refImageUrl] : [];

      if (!isVideo) {
        // ── Image template → /generate ────────────────────────────────────
        const state = {
          action: 'template-use',
          prompt: tmpl.prompt || '',
          modelId: tmpl.model || undefined,
        };
        if (refImageUrls.length > 0) {
          state.sourceUrl = refImageUrls[0];
          state.refImageUrls = refImageUrls;
          state.action = 'use-as-reference';
        }
        navigate('/generate', { state });
        toast.success('Template loaded into Generate Image!');

      } else {
        // ── Video template → /video ────────────────────────────────────────
        const state = {
          action: 'template-use',
          prompt: tmpl.prompt || '',
          modelId: tmpl.model || undefined,
          isMultiShot: tmpl.isMultiShot || false,
          refVideoUrl: tmpl.refVideoUrl || null,
          refAudioUrl: tmpl.refAudioUrl || null,
          endImageUrl: tmpl.endImageUrl || null,
          motionControl: tmpl.motionControl || false,
          lipsync: tmpl.lipsync || false,
        };

        if (tmpl.startImageUrl) {
          // Has a specific start frame → image-to-video mode
          state.sourceUrl = tmpl.startImageUrl;
          state.action = 'image-to-video';
        } else if (refImageUrls.length > 0 || tmpl.refVideoUrl || tmpl.refAudioUrl) {
            // Has reference image(s) or video(s)
            state.sourceUrl = refImageUrls[0] || null;
            state.refImageUrls = refImageUrls;
            state.action = 'use-as-reference';
        }
        // If neither, just pass the prompt + model (text-to-video)
        
        state.motionControl = tmpl.motionControl || false;
        state.lipsync = tmpl.lipsync || false;

        navigate('/video', { state });
        toast.success('Template loaded into Video!');
      }
    } catch (e) {
      console.error('Template use error:', e);
      toast.error('Failed to load template');
    } finally {
      setUsingId(null);
    }
  }, [user, navigate, onShowAuth]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes exploreCardIn {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .explore-card { animation: exploreCardIn 0.35s ease-out both; }
        .explore-card:hover .explore-overlay { opacity: 1 !important; }
        .explore-card:hover .explore-media { transform: scale(1.04); }
        .explore-use-btn {
          background: linear-gradient(135deg, #7c3aed, #a855f7);
          border: none;
          border-radius: 10px;
          color: #fff;
          font-size: 12px;
          font-weight: 800;
          padding: 8px 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
          box-shadow: 0 4px 16px rgba(124,58,237,0.5);
        }
        .explore-use-btn:hover:not(:disabled) {
          transform: translateY(-2px) scale(1.04);
          box-shadow: 0 8px 24px rgba(124,58,237,0.7);
        }
        .explore-use-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
        .explore-search {
          background: var(--s2);
          border: 1px solid var(--b2);
          border-radius: 10px;
          color: var(--t1);
          font-size: 13px;
          padding: 8px 14px 8px 38px;
          outline: none;
          width: 240px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .explore-search:focus {
          border-color: var(--ac);
          box-shadow: 0 0 0 2px var(--acg);
        }
        .explore-search::placeholder { color: var(--t3); }
        @keyframes exploreFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .explore-toolbar {
          flex-wrap: wrap;
          gap: 8px;
          padding: 12px 20px;
        }
        .explore-toolbar-left {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }
        .explore-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-right: 4px;
          min-width: 0;
        }
        .explore-title-text {
          font-weight: 800;
          font-size: 15px;
          white-space: nowrap;
        }
        .explore-search-wrap {
          position: relative;
          display: flex;
          align-items: center;
        }
        .explore-count {
          font-size: 12px;
          color: var(--t3);
          white-space: nowrap;
        }
        .explore-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
          gap: 12px;
        }
        .explore-empty {
          text-align: center;
          padding: 80px 16px;
          color: var(--t3);
        }
        .explore-lb-ov {
          position: fixed;
          inset: 0;
          z-index: 600;
          background: rgba(0,0,10,.88);
          backdrop-filter: blur(18px);
          display: flex;
          align-items: center;
          justify-content: center;
          animation: exploreFadeIn .18s ease;
          padding: 20px;
        }
        .explore-lb-card {
          position: relative;
          background: var(--s1);
          border: 1px solid var(--b2);
          border-radius: 20px;
          overflow: hidden;
          max-width: 520px;
          width: 100%;
          box-shadow: 0 40px 120px rgba(0,0,0,.95);
          animation: exploreCardIn .22s ease-out;
        }
        .explore-lb-close {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 10;
          background: rgba(0,0,0,.55);
          border: 1px solid rgba(255,255,255,.12);
          color: #fff;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background .2s;
          backdrop-filter: blur(8px);
        }
        .explore-lb-close:hover { background: rgba(239,68,68,.7); }
        .explore-lb-media {
          width: 100%;
          display: block;
          max-height: 62vh;
          object-fit: contain;
          background: #000;
        }
        .explore-lb-placeholder {
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(124,58,237,0.12), rgba(34,211,238,0.06));
          color: var(--t3);
        }
        .explore-lb-body { padding: 16px 18px 20px; }
        .explore-lb-label {
          font-size: 11px;
          font-weight: 700;
          color: var(--t3);
          text-transform: uppercase;
          letter-spacing: .08em;
          margin-bottom: 6px;
        }
        .explore-lb-prompt {
          font-size: 13px;
          color: var(--t2);
          line-height: 1.65;
          margin-bottom: 14px;
          max-height: 90px;
          overflow-y: auto;
          scrollbar-width: thin;
        }
        .explore-lb-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .explore-lb-copy,
        .explore-lb-use {
          border: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border-radius: 99px;
          cursor: pointer;
          transition: all .2s;
        }
        .explore-lb-copy {
          background: var(--s2);
          border: 1px solid var(--b3);
          color: var(--t2);
          padding: 7px 16px;
          font-size: 12px;
          font-weight: 600;
        }
        .explore-lb-copy:hover { border-color: rgba(139,92,246,.5); color: var(--ac3); }
        .explore-lb-use {
          background: var(--ac);
          color: #fff;
          padding: 7px 18px;
          font-size: 13px;
          font-weight: 700;
          margin-left: auto;
        }
        .explore-lb-use:hover { opacity: .88; transform: translateY(-1px); }
        @media (hover: none) {
          .explore-card .explore-overlay { opacity: 1 !important; }
          .explore-card:hover { transform: none; }
          .explore-card:hover .explore-media { transform: none; }
        }
        @media (max-width: 640px) {
          .explore-toolbar {
            padding: 10px 12px;
            align-items: stretch;
          }
          .explore-toolbar-left {
            width: 100%;
            justify-content: space-between;
            gap: 6px;
            overflow-x: auto;
            scrollbar-width: none;
          }
          .explore-toolbar-left::-webkit-scrollbar { display: none; }
          .explore-title {
            flex: 0 0 auto;
            margin-right: 2px;
          }
          .explore-title-text { font-size: 14px; }
          .explore-search-wrap {
            width: 100%;
            order: 3;
          }
          .explore-search {
            width: 100%;
            min-height: 40px;
          }
          .explore-count {
            order: 4;
            width: 100%;
            text-align: center;
            font-size: 11px;
          }
          .explore-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
          }
          .explore-card .gov {
            padding: 8px;
            background: linear-gradient(to top, rgba(0,0,0,.86) 0%, rgba(0,0,0,.42) 72%, transparent 100%);
          }
          .explore-card .gm {
            font-size: 9px;
            line-height: 1.35;
          }
          .explore-use-btn {
            padding: 7px 10px;
            font-size: 11px;
            border-radius: 8px;
          }
          .explore-empty { padding: 56px 16px; }
          .explore-lb-ov {
            align-items: flex-end;
            padding: 12px;
          }
          .explore-lb-card {
            max-width: none;
            border-radius: 16px;
          }
          .explore-lb-media { max-height: 54vh; }
          .explore-lb-actions { flex-wrap: wrap; }
          .explore-lb-copy,
          .explore-lb-use {
            flex: 1 1 auto;
            justify-content: center;
            margin-left: 0;
          }
        }
      `}</style>

      <div className="main" style={{ flex: 1 }}>
        {/* ── Top Bar ─────────────────────────────────────────────────────── */}
        <div className="mtbar explore-toolbar">
          <div className="explore-toolbar-left">
            {/* Title */}
            <div className="explore-title">
              <div style={{
                width: 28, height: 28, borderRadius: 8, background: 'rgba(167,139,250,0.15)',
                border: '1px solid rgba(167,139,250,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Icons.Sparkle sz={14} color="#a78bfa" />
              </div>
              <span className="explore-title-text">Templates</span>
            </div>

            {/* Type filter tabs */}
            {TYPE_FILTERS.map(t => (
              <div key={t} className={`mtab ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)}>
                {t}
              </div>
            ))}
          </div>

          <div style={{ flex: 1 }} />

          {/* Search */}
          <div className="explore-search-wrap">
            <div style={{ position: 'absolute', left: 11, color: 'var(--t3)', pointerEvents: 'none' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
            <input
              className="explore-search"
              placeholder="Search templates..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <span className="explore-count">
            {loading ? '...' : `${filtered.length} templates`}
          </span>
        </div>

        {/* ── Body ─────────────────────────────────────────────────────────── */}
        <div className="mbody">
          {loading ? (
            /* ── Skeleton Loading State ── */
            <div className="explore-grid">
              {Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : filtered.length === 0 ? (
            /* ── Empty State ── */
            <div className="explore-empty">
              <div style={{
                width: 64, height: 64, borderRadius: 20, margin: '0 auto 16px',
                background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icons.Sparkle sz={28} color="rgba(167,139,250,0.4)" />
              </div>
              <p style={{ marginTop: 12, fontSize: 15, fontWeight: 700, color: 'var(--t2)' }}>
                No templates found
              </p>
              <p style={{ fontSize: 12, marginTop: 4 }}>
                {search ? 'Try a different search term' : 'No templates have been added yet'}
              </p>
            </div>
          ) : (
            /* ── Template Grid ── */
            <div className="explore-grid">
              {filtered.map((tmpl, idx) => {
                const isVideo = isVideoTemplate(tmpl);
                const mediaSrc = templateMedia(tmpl);
                const isBeingUsed = usingId === (tmpl.id || tmpl.$id);
                return (
                  <div
                    key={tmpl.id || tmpl.$id || idx}
                    className="gitem explore-card"
                    onClick={() => { setCopied(false); setLightboxItem(tmpl); }}
                    style={{ animationDelay: `${Math.min(idx * 0.04, 0.5)}s` }}
                  >
                    {/* ── Media ── */}
                    <div style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'absolute', inset: 0 }}>
                      {mediaSrc ? (
                        isVideo && tmpl.mediaUrl ? (
                          <video
                            className="explore-media"
                            src={mediaSrc}
                            muted loop playsInline
                            onMouseEnter={e => e.target.play()}
                            onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }}
                          />
                        ) : (
                          <img
                            className="explore-media"
                            src={mediaSrc}
                            alt={tmpl.name || tmpl.prompt || ''}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }}
                          />
                        )
                      ) : (
                        /* ── Placeholder when no media ── */
                        <div style={{
                          width: '100%', height: '100%',
                          background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(34,211,238,0.06) 100%)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          gap: 8, color: 'var(--t3)',
                        }}>
                          {isVideo
                            ? <Icons.FilmStrip sz={32} color="rgba(34,211,238,0.3)" />
                            : <Icons.ImgFrame sz={32} color="rgba(139,92,246,0.3)" />
                          }
                        </div>
                      )}
                    </div>

                    {/* ── Type Badge ── */}
                    <TypeBadge isVideo={isVideo} />

                    {/* ── Hover Overlay ── */}
                    <div className="gov explore-overlay" style={{ opacity: 0, transition: 'opacity 0.25s', zIndex: 6 }}>
                      <div className="gm">
                        {/* Name */}
                        {tmpl.name && (
                          <div style={{
                            fontWeight: 800, fontSize: 12,
                            overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                            marginBottom: 3, color: '#fff',
                          }}>
                            {tmpl.name}
                          </div>
                        )}

                        {/* Prompt */}
                        <div style={{
                          fontSize: 10, color: 'rgba(255,255,255,0.65)',
                          overflow: 'hidden', display: '-webkit-box',
                          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          lineHeight: 1.5, marginBottom: 6,
                        }}>
                          {tmpl.prompt || 'No prompt'}
                        </div>

                        {/* Meta row: model + tags */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div style={{
                            fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.45)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%',
                          }}>
                            {tmpl.model || '—'}
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            {tmpl.motionControl && (
                              <span style={{ fontSize: 8, background: 'rgba(6,182,212,0.2)', color: '#22d3ee', border: '1px solid rgba(6,182,212,0.3)', padding: '1px 5px', borderRadius: 99, fontWeight: 800 }}>
                                Motion
                              </span>
                            )}
                            {tmpl.lipsync && (
                              <span style={{ fontSize: 8, background: 'rgba(236,72,153,0.2)', color: '#ec4899', border: '1px solid rgba(236,72,153,0.3)', padding: '1px 5px', borderRadius: 99, fontWeight: 800 }}>
                                Lip
                              </span>
                            )}
                            {tmpl.isMultiShot && (
                              <span style={{ fontSize: 8, background: 'rgba(245,158,11,0.2)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)', padding: '1px 5px', borderRadius: 99, fontWeight: 800 }}>
                                Multi
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Use Button */}
                        <button
                          className="explore-use-btn"
                          disabled={!!isBeingUsed}
                          onClick={e => { e.stopPropagation(); handleUse(tmpl); }}
                          style={{ width: '100%', justifyContent: 'center' }}
                        >
                          {isBeingUsed ? (
                            <>
                              <div style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                              Loading...
                            </>
                          ) : (
                            <>
                              <Icons.Sparkle sz={11} />
                              Use Template
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {lightboxItem && (() => {
        const lb = lightboxItem;
        const isVideo = isVideoTemplate(lb);
        const mediaSrc = templateMedia(lb);
        const prompt = lb.prompt || '';
        const handleCopy = () => {
          if (!prompt) return;
          navigator.clipboard.writeText(prompt).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        };
        return (
          <div className="explore-lb-ov" onClick={() => setLightboxItem(null)}>
            <div className="explore-lb-card" onClick={e => e.stopPropagation()}>
              <button className="explore-lb-close" onClick={() => setLightboxItem(null)}><Icons.X sz={14} /></button>
              {mediaSrc ? (
                isVideo && lb.mediaUrl ? (
                  <video src={mediaSrc} autoPlay muted loop playsInline className="explore-lb-media" />
                ) : (
                  <img src={mediaSrc} alt="" className="explore-lb-media" />
                )
              ) : (
                <div className="explore-lb-placeholder">
                  {isVideo ? <Icons.FilmStrip sz={42} /> : <Icons.ImgFrame sz={42} />}
                </div>
              )}
              <div className="explore-lb-body">
                {prompt ? (
                  <>
                    <div className="explore-lb-label">Prompt</div>
                    <div className="explore-lb-prompt">{prompt}</div>
                  </>
                ) : (
                  <div className="explore-lb-label" style={{ marginBottom: 14 }}>No prompt available</div>
                )}
                <div className="explore-lb-actions">
                  {prompt && (
                    <button className="explore-lb-copy" onClick={handleCopy}>
                      {copied ? (
                        <><Icons.Check sz={12} /> Copied!</>
                      ) : (
                        <>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                          Copy Prompt
                        </>
                      )}
                    </button>
                  )}
                  <button className="explore-lb-use" onClick={() => { setLightboxItem(null); handleUse(lb); }}>
                    <Icons.Sparkle sz={12} />
                    Use Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}
